import { Controller } from "@hotwired/stimulus";
import DOMPurify from "dompurify";

const ALLOWED_TAGS = [
    "a",
    "b",
    "br",
    "div",
    "em",
    "font",
    "i",
    "img",
    "li",
    "ol",
    "p",
    "span",
    "strong",
    "table",
    "tbody",
    "td",
    "th",
    "thead",
    "tr",
    "u",
    "ul",
];

const PURIFY_CONFIG = {
    ALLOWED_TAGS,
    ADD_DATA_URI_TAGS: ["img"],
    ALLOWED_ATTR: [
        "align",
        "alt",
        "border",
        "cellpadding",
        "cellspacing",
        "class",
        "color",
        "dir",
        "face",
        "height",
        "href",
        "rel",
        "size",
        "src",
        "style",
        "target",
        "title",
        "valign",
        "width",
    ],
    ALLOW_DATA_ATTR: false,
};

const ALLOWED_IMAGE_TYPES = new Set(["image/png", "image/jpeg", "image/jpg", "image/gif", "image/webp"]);
const MAX_INLINE_IMAGE_BYTES = 512 * 1024;

type HtmxRequestEvent = CustomEvent<{
    failed?: boolean;
    successful?: boolean;
}>;

export default class extends Controller<HTMLFormElement> {
    static targets = ["editor", "error", "imageInput", "input", "newEmailMode", "replyForwardMode"];
    static values = {
        imageError: String,
        initialHtml: String,
        saveError: String,
    };

    declare readonly editorTarget: HTMLElement;
    declare readonly imageInputTarget: HTMLInputElement;
    declare readonly hasImageInputTarget: boolean;
    declare readonly inputTarget: HTMLTextAreaElement;
    declare readonly newEmailModeTarget: HTMLSelectElement;
    declare readonly hasNewEmailModeTarget: boolean;
    declare readonly replyForwardModeTarget: HTMLSelectElement;
    declare readonly hasReplyForwardModeTarget: boolean;
    declare readonly imageErrorValue: string;
    declare readonly hasImageErrorValue: boolean;
    declare readonly initialHtmlValue: string;
    declare readonly hasInitialHtmlValue: boolean;
    declare readonly saveErrorValue: string;
    declare readonly hasSaveErrorValue: boolean;

    private hadInitialSignature = false;
    private signatureModesTouched = false;

    connect(): void {
        const initialHtml = this.hasInitialHtmlValue ? this.initialHtmlValue : this.inputTarget.value;
        this.hadInitialSignature = initialHtml.trim().length > 0;
        this.editorTarget.innerHTML = initialEditorHtml(initialHtml);
        this.sync();
    }

    sync(): void {
        this.inputTarget.value = signatureHtmlFromEditor(this.editorTarget.innerHTML);
        this.autoselectSignatureDefaults();
    }

    normalize(): void {
        const rawText = this.editorTarget.textContent?.trim() ?? "";
        const renderedHtml = this.editorTarget.innerHTML;

        if (looksLikeSignatureHtml(rawText) && renderedHtml.includes("&lt;")) {
            this.editorTarget.innerHTML = sanitizeSignatureHtml(rawText);
        }

        this.sync();
    }

    paste(event: ClipboardEvent): void {
        const html = event.clipboardData?.getData("text/html") ?? "";
        const plainText = event.clipboardData?.getData("text/plain") ?? "";
        const imageFile = Array.from(event.clipboardData?.files ?? []).find((file) => file.type.startsWith("image/"));
        const pastedHtml = looksLikeSignatureHtml(plainText)
            ? sanitizeSignatureHtml(plainText)
            : html
              ? sanitizeSignatureHtml(html)
              : plainTextToHtml(plainText);

        if (imageFile) {
            event.preventDefault();
            this.insertImageFile(imageFile, pastedHtml);
            return;
        }

        if (!pastedHtml) {
            return;
        }

        event.preventDefault();
        insertHtmlAtSelection(this.editorTarget, pastedHtml);
        this.sync();
    }

    uploadImage(): void {
        const imageFile = this.imageInputTarget.files?.[0];
        if (imageFile) {
            this.insertImageFile(imageFile, "", true);
        }
        this.imageInputTarget.value = "";
    }

    dragOver(event: DragEvent): void {
        if (!this.hasFiles(event)) {
            return;
        }

        event.preventDefault();
        if (event.dataTransfer) {
            event.dataTransfer.dropEffect = "copy";
        }
        this.setDropzoneActive(true);
    }

    dragLeave(event: DragEvent): void {
        if (event.relatedTarget instanceof Node && this.editorTarget.contains(event.relatedTarget)) {
            return;
        }

        this.setDropzoneActive(false);
    }

    drop(event: DragEvent): void {
        if (!this.hasFiles(event)) {
            return;
        }

        event.preventDefault();
        this.setDropzoneActive(false);
        const imageFile = event.dataTransfer?.files[0];
        if (imageFile) {
            this.insertImageFile(imageFile, "", true);
        }
    }

    markSignatureModesTouched(): void {
        this.signatureModesTouched = true;
    }

    clearError(): void {
        const errorElement = this.findErrorElement();
        if (errorElement) {
            errorElement.textContent = "";
            errorElement.classList.add("hidden");
        }
        this.editorTarget.removeAttribute("aria-invalid");
        if (this.hasImageInputTarget) {
            this.imageInputTarget.setCustomValidity("");
            this.imageInputTarget.removeAttribute("aria-invalid");
        }
    }

    handleSaveResult(event: HtmxRequestEvent): void {
        if (event.detail?.successful && !event.detail.failed) {
            this.clearError();
            return;
        }

        this.reportSaveError();
    }

    handleSaveError(): void {
        this.reportSaveError();
    }

    private insertImageFile(imageFile: File, pastedHtml = "", appendToEditor = false): void {
        if (!ALLOWED_IMAGE_TYPES.has(imageFile.type.toLowerCase()) || imageFile.size > MAX_INLINE_IMAGE_BYTES) {
            this.reportImageError();
            return;
        }

        if (this.hasImageInputTarget) {
            this.imageInputTarget.setCustomValidity("");
        }
        this.clearError();

        const reader = new FileReader();
        reader.addEventListener("load", () => {
            if (typeof reader.result !== "string") {
                this.reportImageError();
                return;
            }

            const container = document.createElement("div");
            container.innerHTML = pastedHtml;
            const image = container.querySelector("img") ?? document.createElement("img");
            image.src = reader.result;
            if (!image.hasAttribute("alt")) {
                image.alt = "";
            }
            if (!image.parentNode) {
                container.appendChild(image);
            }

            const imageHtml = sanitizeSignatureHtml(container.innerHTML);
            if (appendToEditor) {
                this.editorTarget.insertAdjacentHTML("beforeend", imageHtml);
                this.editorTarget.focus();
            } else {
                insertHtmlAtSelection(this.editorTarget, imageHtml);
            }
            this.editorTarget.dispatchEvent(new Event("input", { bubbles: true }));
        });
        reader.addEventListener("error", () => this.reportImageError());
        reader.readAsDataURL(imageFile);
    }

    private reportImageError(): void {
        const message = this.hasImageErrorValue ? this.imageErrorValue : "Unsupported signature image.";
        this.imageInputTarget.setCustomValidity(message);
        this.showError(message);
    }

    private reportSaveError(): void {
        this.showError(
            this.hasSaveErrorValue ? this.saveErrorValue : "The signature could not be saved. Please try again.",
        );
    }

    private showError(message: string): void {
        const errorElement = this.findErrorElement() ?? this.createErrorElement();
        errorElement.textContent = message;
        errorElement.classList.remove("hidden");
        this.editorTarget.setAttribute("aria-invalid", "true");
        if (this.hasImageInputTarget) {
            this.imageInputTarget.setAttribute("aria-invalid", "true");
        }
    }

    private findErrorElement(): HTMLElement | null {
        return this.element.querySelector<HTMLElement>(
            '[data-account-outbound-signature-editor-target~="error"]',
        );
    }

    private createErrorElement(): HTMLElement {
        const errorElement = document.createElement("p");
        const describedByIds =
            this.imageInputTarget.getAttribute("aria-describedby")?.trim().split(/\s+/) ?? [];
        const errorId = describedByIds[describedByIds.length - 1];
        if (errorId) {
            errorElement.id = errorId;
        }
        errorElement.className = "hidden text-sm font-semibold text-red-700 dark:text-red-300";
        errorElement.setAttribute("role", "alert");
        errorElement.setAttribute("aria-live", "polite");
        errorElement.setAttribute("data-account-outbound-signature-editor-target", "error");
        errorElement.setAttribute("data-testid", "account-outbound-signature-error");
        const editorContainer = this.editorTarget.parentElement;
        if (editorContainer) {
            editorContainer.insertAdjacentElement("afterend", errorElement);
        } else {
            this.element.append(errorElement);
        }
        return errorElement;
    }

    private hasFiles(event: DragEvent): boolean {
        return Array.from(event.dataTransfer?.types ?? []).includes("Files");
    }

    private setDropzoneActive(active: boolean): void {
        this.editorTarget.dataset.dropZoneActive = String(active);
        this.editorTarget.classList.toggle("ring-4", active);
        this.editorTarget.classList.toggle("ring-indigo-400", active);
        this.editorTarget.classList.toggle("bg-indigo-50", active);
        this.editorTarget.classList.toggle("shadow-lg", active);
        this.editorTarget.classList.toggle("dark:bg-indigo-950/40", active);
        this.editorTarget.classList.toggle("scale-[1.01]", active);
    }

    private autoselectSignatureDefaults(): void {
        if (this.hadInitialSignature || this.signatureModesTouched || !this.inputTarget.value.trim()) {
            return;
        }

        if (this.hasNewEmailModeTarget && this.newEmailModeTarget.value === "provider") {
            this.newEmailModeTarget.value = "signature";
        }

        if (this.hasReplyForwardModeTarget && this.replyForwardModeTarget.value === "provider") {
            this.replyForwardModeTarget.value = "signature";
        }
    }
}

function initialEditorHtml(rawHtml: string): string {
    const trimmed = rawHtml.trim();
    if (!trimmed) {
        return "";
    }

    if (looksLikeSignatureHtml(trimmed)) {
        return sanitizeSignatureHtml(trimmed);
    }

    return plainTextToHtml(trimmed);
}

function signatureHtmlFromEditor(editorHtml: string): string {
    const sanitized = sanitizeSignatureHtml(editorHtml).trim();
    if (!sanitized) {
        return "";
    }

    if (looksLikeSignatureHtml(sanitized)) {
        return sanitized;
    }

    return `<div>${sanitized}</div>`;
}

function sanitizeSignatureHtml(html: string): string {
    return DOMPurify.sanitize(html, PURIFY_CONFIG);
}

function looksLikeSignatureHtml(html: string): boolean {
    const tagPattern = new RegExp(`</?(${ALLOWED_TAGS.join("|")})(\\s|>|/)`, "i");
    return tagPattern.test(html);
}

function plainTextToHtml(text: string): string {
    const escaped = escapeHtml(text.trim());
    if (!escaped) {
        return "";
    }

    return `<div>${escaped.replace(/\r\n|\r|\n/g, "<br>")}</div>`;
}

function escapeHtml(text: string): string {
    return text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

function insertHtmlAtSelection(editor: HTMLElement, html: string): void {
    editor.focus();
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
        editor.insertAdjacentHTML("beforeend", html);
        return;
    }

    const range = selection.getRangeAt(0);
    if (!editor.contains(range.commonAncestorContainer)) {
        editor.insertAdjacentHTML("beforeend", html);
        return;
    }

    const fragmentContainer = document.createElement("div");
    fragmentContainer.innerHTML = html;
    const fragment = document.createDocumentFragment();
    let lastInsertedNode: ChildNode | null = null;

    while (fragmentContainer.firstChild) {
        lastInsertedNode = fragmentContainer.firstChild;
        fragment.appendChild(fragmentContainer.firstChild);
    }

    range.deleteContents();
    range.insertNode(fragment);

    if (lastInsertedNode) {
        range.setStartAfter(lastInsertedNode);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
    }
}
